import * as React from "react";
import { Link as WouterLink, type LinkProps as WouterLinkProps } from "wouter";

import { resolveAnalyticsElement } from "@/lib/analytics/analyticsElement";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";

type InternalLinkProps = Omit<WouterLinkProps, "onClick"> & {
  /**
   * Structured element id wiring click analytics: stamps the `data-analytics-*` marker
   * attributes and queues `fe_element_clicked` on click.
   */
  analyticsElementId?: AnalyticsElementId;
  className?: string;
  style?: React.CSSProperties;
  scrollToTop?: boolean;
  // This component always renders an anchor. Wouter's union of anchor/generic
  // handlers otherwise removes contextual typing from callers' event parameters.
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type LinkClickHandler = NonNullable<WouterLinkProps["onClick"]>;
type LinkClickEvent = Parameters<LinkClickHandler>[0];

/**
 * Wouter link for in-app SPA navigation; scrolls to the top on click by default.
 * Use `ExternalLink` for navigation that leaves the SPA.
 */
export const InternalLink = ({
  analyticsElementId,
  scrollToTop = true,
  onClick,
  href,
  children,
  ...props
}: InternalLinkProps) => {
  const analyticsElement = resolveAnalyticsElement(analyticsElementId);

  const handleClick: LinkClickHandler = (event: LinkClickEvent) => {
    if (scrollToTop && typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }

    analyticsElement?.emitClick();

    if (typeof onClick === "function") {
      // TODO: [Type Enforce] Justified 'as': LinkClickEvent is MouseEvent<HTMLAnchorElement>|MouseEvent<Element>
      // (Wouter's Link onClick union), but the narrowed onClick only accepts MouseEvent<HTMLAnchorElement>.
      // The cast widens the parameter signature to accept the broader event this handler receives.
      (onClick as (event: LinkClickEvent) => void)(event);
    }
  };

  return (
    // @ts-expect-error Wouter Link typing is broad; analytics attributes flow through to the anchor.
    <WouterLink href={href} onClick={handleClick} {...analyticsElement?.attributes} {...props}>
      {children}
    </WouterLink>
  );
};
